/**
 * Brand-themed toast + confirm-dialog utilities.
 *
 * Replaces the ugly native window.alert() and window.confirm() dialogs with
 * beautiful, on-brand modals that fit the Find Your Naseeb aesthetic.
 *
 * Usage:
 *   import { toast, confirmDialog, DialogProvider } from "@/lib/ui";
 *   toast.success("Interesse sendt");
 *   const ok = await confirmDialog({ title: "Slet demo-brugere?", body: "..." });
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";

// ---------- Toast API (sonner-backed, branded) ----------
export const toast = {
  success: (msg, opts) => sonnerToast.success(msg, { icon: <CheckCircle2 className="h-4 w-4 text-olive-600" />, ...opts }),
  error:   (msg, opts) => sonnerToast.error(msg,   { icon: <XCircle className="h-4 w-4 text-destructive" />, ...opts }),
  info:    (msg, opts) => sonnerToast(msg,          { icon: <Info className="h-4 w-4 text-olive-600" />, ...opts }),
  warning: (msg, opts) => sonnerToast(msg,          { icon: <AlertTriangle className="h-4 w-4 text-gold-600" />, ...opts }),
};

export function BrandToaster() {
  return (
    <Sonner
      position="top-center"
      richColors={false}
      closeButton
      theme="light"
      toastOptions={{
        classNames: {
          toast:
            "!bg-sand-50 !border !border-sand-300 !shadow-xl !rounded-2xl " +
            "!text-brown-700 !font-sans !text-sm !p-4 !min-w-[280px]",
          title: "!font-serif !text-base !text-olive-700",
          description: "!text-brown-500 !text-xs",
          success: "!border-olive-500/30 !bg-olive-50",
          error: "!border-destructive/30 !bg-destructive/5",
          closeButton: "!bg-sand-100 !border-sand-300 !text-brown-500 hover:!bg-sand-200",
        },
      }}
    />
  );
}

// ---------- Confirm dialog API ----------
const ConfirmContext = createContext(null);

/** Renders the confirm-modal portal and exposes `confirm({title, body, ...})`. */
export function DialogProvider({ children }) {
  const [state, setState] = useState(null); // {title, body, confirmText, cancelText, tone, resolve}
  const openRef = useRef(null);

  const openConfirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({
        title: options.title || "Er du sikker?",
        body: options.body || "",
        confirmText: options.confirmText || "Bekræft",
        cancelText: options.cancelText || "Annullér",
        tone: options.tone || "primary", // "primary" | "danger"
        resolve,
      });
      // Autofocus after mount
      setTimeout(() => openRef.current?.focus(), 30);
    });
  }, []);

  const close = (result) => {
    if (state?.resolve) state.resolve(result);
    setState(null);
  };

  // Keep global-callable ref so plain `confirmDialog(...)` works.
  useEffect(() => {
    globalOpenConfirm = openConfirm;
    return () => { globalOpenConfirm = null; };
  }, [openConfirm]);

  // Escape closes with false
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <ConfirmContext.Provider value={openConfirm}>
      {children}
      {state && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => close(false)}
          data-testid="confirm-dialog-backdrop">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl bg-sand-50 border border-sand-300 shadow-2xl p-6 sm:p-7 animate-in zoom-in-95 duration-200"
            role="dialog" aria-modal="true"
            data-testid="confirm-dialog">
            <div className="flex items-start gap-3">
              <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                state.tone === "danger" ? "bg-destructive/10 text-destructive" : "bg-olive-100 text-olive-700"
              }`}>
                {state.tone === "danger" ? <AlertTriangle className="h-5 w-5" /> : <Info className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-serif text-xl text-olive-700" data-testid="confirm-title">{state.title}</h2>
                {state.body && <p className="mt-1.5 text-sm text-brown-600 whitespace-pre-line" data-testid="confirm-body">{state.body}</p>}
              </div>
              <button
                onClick={() => close(false)}
                aria-label="Luk"
                className="shrink-0 rounded-full h-8 w-8 flex items-center justify-center text-brown-500 hover:bg-sand-200"
                data-testid="confirm-close-x">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => close(false)}
                className="rounded-full border border-sand-300 bg-white hover:bg-sand-100 text-brown-600 font-medium px-5 py-2.5 text-sm transition"
                data-testid="confirm-cancel">
                {state.cancelText}
              </button>
              <button
                ref={openRef}
                onClick={() => close(true)}
                className={`rounded-full font-medium px-5 py-2.5 text-sm text-white transition shadow-sm ${
                  state.tone === "danger"
                    ? "bg-destructive hover:bg-destructive/90"
                    : "bg-olive-500 hover:bg-olive-600"
                }`}
                data-testid="confirm-ok">
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </ConfirmContext.Provider>
  );
}

// Global reference (populated by DialogProvider). Enables plain `confirmDialog(...)`
// calls from anywhere without needing hook wiring.
let globalOpenConfirm = null;

/** Programmatic confirm — returns Promise<boolean>. */
export function confirmDialog(options) {
  if (!globalOpenConfirm) {
    // Fallback if DialogProvider isn't mounted (SSR / testing)
    return Promise.resolve(window.confirm(options?.body || options?.title || "Er du sikker?"));
  }
  return globalOpenConfirm(options);
}

/** Hook to call `confirm({...})` inside a component. */
export function useConfirm() {
  const openConfirm = useContext(ConfirmContext);
  if (!openConfirm) throw new Error("useConfirm must be used inside <DialogProvider>");
  return openConfirm;
}
